import { getAppletsUrl } from '@cartwhl/common/utils/getAppletsUrl';
import { getStudioUrl } from '@cartwhl/common/utils/getStudioUrl';

export type NavItem =
  | {
    id: string;
    label: string;
    href: string;
    subtitle?: string;
    external?: boolean;
  }
  | { id: string; label: string; subtitle?: string; unavailable: string };

export interface NavMenuSection {
  id: string;
  label: string;
  items: readonly NavItem[];
}

export const modelsItems = [
  {
    id: 'comic',
    label: 'Comic',
    subtitle: 'AI Performance Capture',
    href: '/model/comic',
  },
  {
    id: 'swing',
    label: 'Swing',
    subtitle: 'Text-to-motion',
    href: '/model/swing',
  },
] as const satisfies readonly NavItem[];

export const productMenuSections: readonly NavMenuSection[] = [
  {
    id: 'apps',
    label: 'Apps',
    items: [
      {
        id: 'performance-capture',
        label: 'Performance Capture',
        subtitle: 'Turn any video into production-ready motion',
        href: '/product/performance-capture',
      },
      {
        id: 'motion-generation',
        label: 'Performance Generation',
        subtitle: 'Creates human motion from a text prompt in seconds',
        href: '/product/performance-generation',
      },
      {
        id: 'motion-editor',
        label: 'Performance Editor',
        subtitle: 'Motion blending & reshaping',
        href: '/product/performance-editor',
      },
      {
        id: 'canvas',
        label: 'Canvas',
        subtitle: 'Create and prototype in our comprehensive suite',
        href: getStudioUrl(false),
        external: true,
      },
    ],
  },
  {
    id: 'pipeline-tools',
    label: 'Pipeline Tools',
    items: [
      {
        id: 'cartographer',
        label: 'Cartographer',
        subtitle: 'Universal motion retargeting',
        href: '/product/cartographer',
      },
      {
        id: 'mcp',
        label: 'MCP',
        subtitle: 'Connect AI agents to Cartwheel',
        href: 'https://github.com/Cartwhl/cartwheel-mcp',
        external: true,
      },
      {
        id: 'api',
        label: 'API',
        subtitle: 'Production at scale',
        href: 'https://api-docs.getcartwheel.com/',
        external: true,
      },
      {
        id: 'plugins',
        label: 'Plugins',
        subtitle: 'Cartwheel’s Capabilities where you already work',
        href: '/plugins',
      },
    ],
  },
];

export const productItems = productMenuSections.flatMap(
  (section) => section.items,
);

export const pricingItems = [
  { id: 'plans', label: 'Plans', href: '/pricing' },
  {
    id: 'credits',
    label: 'How credits work',
    href: '/pricing#pricing-credits-explainer',
  },
] as const satisfies readonly NavItem[];

export const learnItems = [
  {
    id: 'canvas-docs',
    label: 'Canvas Docs',
    subtitle: 'Learn how to use Canvas',
    href: 'https://docs.getcartwheel.com/docs/getting-started/introduction',
    external: true,
  },
  {
    id: 'api-docs',
    label: 'API Docs',
    subtitle: 'API reference & guides',
    href: 'https://api-docs.getcartwheel.com/',
    external: true,
  },
  {
    id: 'blog',
    label: 'Blog',
    subtitle: 'Notes from the team building Cartwheel',
    href: '/blog',
  },
] as const satisfies readonly NavItem[];

export const navigation = [
  { id: 'models', label: 'Models', items: modelsItems },
  { id: 'products', label: 'Products', items: productItems },
  { id: 'research', label: 'Research', href: '/research' },
  { id: 'pricing', label: 'Pricing', items: pricingItems },
  { id: 'company', label: 'Company', href: '/company' },
  { id: 'learn', label: 'Learn', items: learnItems },
] as const;

export type MobileNavigationItem =
  | { id: string; label: string; href: string }
  | { id: string; label: string; items: readonly NavItem[] };

export const mobileNavigation: readonly MobileNavigationItem[] = [
  {
    id: 'models',
    label: 'Models',
    items: modelsItems,
  },
  {
    id: 'products',
    label: 'Products',
    items: productItems,
  },
  { id: 'research', label: 'Research', href: '/research' },
  { id: 'pricing', label: 'Pricing', items: pricingItems },
  { id: 'company', label: 'Company', href: '/company' },
  { id: 'learn', label: 'Learn', items: learnItems },
];
